import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Download } from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { Kanban } from '@/components/Kanban';
import { NovaDemandaModal } from '@/components/NovaDemandaModal';
import { EditDemandaModalV3 as EditDemandaModal } from '@/components/EditDemandaModalV3';
import { KanbanFilters, type FilterState } from '@/components/KanbanFilters';
import { KanbanSkeleton } from '@/components/Skeleton';
import { api } from '@/lib/api';
import type { StatusDemanda } from '@/lib/types';
import { downloadCSV } from '@/lib/export';

const KPI_DEFS: { label: string; status: StatusDemanda; cls: string }[] = [
  { label: 'Briefing',     status: 'Triagem',              cls: 'border-blue/40 text-blue-light' },
  { label: 'Em produção',  status: 'Em produção',          cls: 'border-orange/40 text-orange' },
  { label: 'Em revisão',   status: 'Em revisão',           cls: 'border-purple/40 text-purple' },
  { label: 'Aguardando',   status: 'Aguardando aprovação', cls: 'border-pink/40 text-pink' },
  { label: 'Finalizadas',  status: 'Publicada',            cls: 'border-green/40 text-green' },
];

export function Producao() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement>(null);
  const [filters, setFilters] = useState<FilterState>({
    search: '', solicitanteId: '', prioridade: '', origemTipo: '',
    tipoProduto: '', tipoEntrega: 'producao', executorId: '', areaId: '', eventoId: '', prazo: '',
  });
  const [highlightStatus, setHighlightStatus] = useState<StatusDemanda | null>(null);

  const demandasQ = useQuery({ queryKey: ['demandas'],     queryFn: () => api.demandas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });
  const pessoasQ = useQuery({ queryKey: ['pessoas'],       queryFn: () => api.pessoas.list().then((r) => r.data) });
  const areasQ = useQuery({ queryKey: ['areas'],           queryFn: () => api.areas.list().then((r) => r.data) });
  const eventosQ = useQuery({ queryKey: ['eventos'],       queryFn: () => api.eventos.list().then((r) => r.data) });
  const contratosQ = useQuery({ queryKey: ['contratos'],   queryFn: () => api.contratos.list().then((r) => r.data) });
  const episodiosQ = useQuery({ queryKey: ['episodios'],   queryFn: () => api.episodios.list().then((r) => r.data) });

  const demandas = demandasQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];
  const pessoas = pessoasQ.data ?? [];
  const areas = areasQ.data ?? [];
  const eventos = eventosQ.data ?? [];
  const contratos = contratosQ.data ?? [];
  const episodios = episodiosQ.data ?? [];

  useEffect(() => {
    let changed = false;
    if (params.get('nova') === '1') { setModalOpen(true); changed = true; }
    if (params.get('busca') === '1') {
      requestAnimationFrame(() => searchRef.current?.focus());
      changed = true;
    }
    const abrir = params.get('abrir');
    if (abrir) { setEditId(abrir); changed = true; }
    const solicitante = params.get('solicitante');
    if (solicitante) {
      setFilters((prev) => ({ ...prev, solicitanteId: solicitante }));
      changed = true;
    }
    const evento = params.get('evento');
    if (evento) {
      setFilters((prev) => ({ ...prev, eventoId: evento }));
      changed = true;
    }
    if (changed) setParams({}, { replace: true });
  }, [params, setParams]);

  const solById = useMemo(() => new Map(solicitantes.map((s) => [s.id, s])), [solicitantes]);
  const pessoaById = useMemo(() => new Map(pessoas.map((p) => [p.id, p.nome])), [pessoas]);
  const tiposProduto = useMemo(() => {
    const set = new Set<string>();
    for (const d of demandas) if (d.tipoProduto) set.add(d.tipoProduto);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [demandas]);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const weekEnd = new Date(now);
    weekEnd.setDate(weekEnd.getDate() + (7 - weekEnd.getDay())); // end of week (Sunday)
    const weekEndStr = weekEnd.toISOString().slice(0, 10);

    return demandas.filter((d) => {
      if (q && !(d.titulo.toLowerCase().includes(q) || (d.pauta ?? '').toLowerCase().includes(q))) return false;
      if (filters.solicitanteId && d.solicitanteId !== filters.solicitanteId) return false;
      if (filters.eventoId && d.eventoId !== filters.eventoId) return false;
      if (filters.prioridade && d.prioridade !== filters.prioridade) return false;
      if (filters.origemTipo && d.origem !== filters.origemTipo) return false;
      if (filters.tipoProduto && d.tipoProduto !== filters.tipoProduto) return false;
      if (filters.tipoEntrega && (d.tipoEntrega ?? 'producao') !== filters.tipoEntrega) return false;
      if (filters.executorId && d.executorId !== filters.executorId) return false;
      if (filters.areaId && !(d.areasIds ?? []).includes(filters.areaId)) return false;
      if (filters.prazo) {
        const sla = d.prazoSla?.slice(0, 10);
        const done = d.status === 'Publicada' || d.status === 'Arquivada' || d.status === 'Cancelada';
        if (filters.prazo === 'atrasado') {
          if (!sla || sla >= todayStr || done) return false;
        } else if (filters.prazo === 'hoje') {
          if (sla !== todayStr) return false;
        } else if (filters.prazo === 'semana') {
          if (!sla || sla < todayStr || sla > weekEndStr) return false;
        } else if (filters.prazo === 'sem-prazo') {
          if (sla) return false;
        }
      }
      if (highlightStatus && d.status !== highlightStatus) return false;
      return true;
    });
  }, [demandas, filters, highlightStatus, solById]);

  const counts = useMemo(() => {
    const m: Partial<Record<StatusDemanda, number>> = {};
    for (const d of demandas) m[d.status] = (m[d.status] ?? 0) + 1;
    return m;
  }, [demandas]);

  return (
    <div>
      <PageHeader
        title="Demandas"
        action={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => {
              const headers = ['Título', 'Status', 'Prioridade', 'Solicitante', 'Executor', 'Prazo SLA', 'Publicação', 'Origem', 'Tipo'];
              const rows = filtered.map(d => [
                d.titulo, d.status, d.prioridade,
                solById.get(d.solicitanteId)?.nome ?? '', pessoaById.get(d.executorId ?? '') ?? '',
                d.prazoSla ?? '', d.dataPublicacao ?? '', d.origem, d.tipoProduto ?? '',
              ]);
              downloadCSV(`demandas_${new Date().toISOString().slice(0,10)}.csv`, headers, rows);
            }} className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Exportar
            </button>
            <button type="button" onClick={() => setModalOpen(true)} className="btn-primary !py-1.5 !px-3 text-xs">
              <Plus className="w-3.5 h-3.5" />
              Nova demanda
            </button>
          </div>
        }
      />

      <div className="px-6 py-3 space-y-3">
        <div className="grid grid-cols-5 gap-2">
          {KPI_DEFS.map((k) => {
            const active = highlightStatus === k.status;
            return (
              <button
                key={k.status}
                type="button"
                onClick={() => setHighlightStatus(active ? null : k.status)}
                className={clsx(
                  'bg-panel-2 border border-border border-2 rounded-md py-2 px-2 text-left transition-colors',
                  k.cls,
                  active ? 'ring-1 ring-blue/50' : 'hover:bg-panel-2/70',
                )}
              >
                <div className="text-base font-black">{counts[k.status] ?? 0}</div>
                <div className="text-[9px] uppercase tracking-wider text-muted">{k.label}</div>
              </button>
            );
          })}
        </div>

        <KanbanFilters
          state={filters}
          onChange={setFilters}
          solicitantes={solicitantes}
          pessoas={pessoas}
          areas={areas}
          tiposProduto={tiposProduto}
          eventos={eventos.map((e) => ({ id: e.id, nome: e.nome }))}
          totalDemandas={demandas.length}
          filteredCount={filtered.length}
          searchRef={searchRef}
        />

        {highlightStatus && (
          <div className="text-[11px] text-blue-light bg-blue/5 border border-blue/20 rounded px-2 py-1 inline-flex items-center gap-2">
            Filtrando: {highlightStatus}
            <button type="button" className="text-muted hover:text-text" onClick={() => setHighlightStatus(null)}>limpar</button>
          </div>
        )}

        {demandasQ.isLoading && <KanbanSkeleton />}
        {demandasQ.error && (
          <div className="card text-sm text-red">Erro ao carregar demandas. Tenta recarregar a página.</div>
        )}
        {!demandasQ.isLoading && !demandasQ.error && (
          <Kanban demandas={filtered} solicitantes={solicitantes} areas={areas} onCardClick={setEditId} />
        )}
      </div>

      <NovaDemandaModal open={modalOpen} onClose={() => setModalOpen(false)} solicitantes={solicitantes} areas={areas} pessoas={pessoas} eventos={eventos} contratos={contratos} episodios={episodios} />
      <EditDemandaModal
        demandaId={editId} demandas={demandas} solicitantes={solicitantes}
        pessoas={pessoas} areas={areas} episodios={episodios} onClose={() => setEditId(null)}
      />
    </div>
  );
}
